<template>
<div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">

  <!-- Sidebar -->
  <HrSidebar :isOpen="isSidebarOpen" />

  <!-- Main -->
  <main 
    class="flex-1 overflow-x-hidden transition-all duration-300"
    :class="{'ml-64': isSidebarOpen, 'ml-0': !isSidebarOpen}"
  >

  <!-- Navbar -->
  <header class="fixed top-0 z-50 w-full bg-white dark:bg-gray-800 shadow-sm">
    <HrTopbar
      :page-title="currentPageTitle"
      :is-sidebar-open="isSidebarOpen"
      :is-dark-mode="isDarkMode"
      @toggle-sidebar="toggleSidebar"
      @toggle-dark-mode="toggleDarkMode"
    />
  </header>

  <!-- Page Content -->
  <div class="p-6 mt-16">

    <!-- Top Bar -->
    <div class="flex justify-between items-center mb-8 p-4">

      <input
        v-model="search"
        type="text"
        placeholder="Search criteria..."
        class="border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 w-72 focus:outline-none focus:ring-2 focus:ring-secondary dark:bg-gray-700 dark:text-white"
      />

      <button
        @click="openCreate"
        class="bg-[#74bd54] text-white px-5 py-2 rounded-lg hover:bg-[#63a43f] shadow"
      >
        + Add Criteria
      </button>

    </div>

    <!-- Grid -->
    <div
      v-if="filteredCriteria.length"
      class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6"
    >

      <div
        v-for="item in filteredCriteria"
        :key="item.id"
        class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow hover:shadow-xl transition"
      >

        <div class="flex justify-between items-start mb-4">

          <div>
            <h3 class="font-semibold text-gray-800 dark:text-white">
              {{ item.name }}
            </h3>

            <span class="text-xs text-gray-500">
              Max Score: {{ item.max_score }}
            </span>
          </div>

          <!-- <span 
            class="text-xs px-2 py-1 rounded"
            :class="item.status ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'"
          >
            {{ item.status ? 'Active' : 'Inactive' }}
          </span> -->

        </div>

        <!-- <p class="text-sm text-gray-500 mb-2">
          Role: {{ item.position_title || 'All Roles' }}
        </p>

        <p class="text-sm text-gray-400">
          Order: {{ item.display_order }}
        </p> -->

        <!-- Actions -->
        <div class="flex justify-end space-x-4 mt-4 text-sm">
          <button @click="editItem(item)" class="text-blue-600 hover:underline">
            Edit
          </button>

          <button @click="deleteItem(item.id)" class="text-red-500 hover:underline">
            Delete
          </button>
        </div>

      </div>

    </div>

    <!-- Empty -->
    <div v-else class="text-center text-gray-400 mt-20">
      No criteria yet
    </div>

    <!-- Modal -->
    <Transition name="modal">
    <div
      v-if="showModal"
      class="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
    >

      <div class="bg-white dark:bg-gray-800 rounded-xl p-6 w-[420px] shadow-lg">

        <h3 class="text-lg font-semibold text-[#035a76] mb-6">
          {{ editing ? 'Edit Criteria' : 'Create Criteria' }}
        </h3>

        <form @submit.prevent="saveItem" class="space-y-4">

          <input
            v-model="form.name"
            type="text"
            placeholder="Criteria Name"
            required
            class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white"
          />

          <input
            v-model="form.max_score"
            type="number"
            placeholder="Max Score"
            class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white"
          />

          <!-- <select
            v-model="form.role_id"
            class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white"
          >
            <option value="">All Roles</option>
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.title }}
            </option>
          </select> -->
<!-- 
          <input
            v-model="form.display_order"
            type="number"
            placeholder="Display Order"
            class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white"
          /> -->
<!-- 
          <select
            v-model="form.status"
            class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white"
          >
            <option :value="1">Active</option>
            <option :value="0">Inactive</option>
          </select> -->

          <div class="flex justify-end space-x-3 pt-2">

            <button type="button" @click="closeModal" class="px-4 py-2 bg-gray-300 rounded">
              Cancel
            </button>

            <button type="submit" class="px-4 py-2 bg-[#74bd54] text-white rounded hover:bg-[#63a43f]">
              Save
            </button>

          </div>

        </form>

      </div>

    </div>
    </Transition>

  </div>

  </main>
</div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue"
import api from "@/services/api"
import HrSidebar from "../../components/hr/HrSidebar.vue"
import HrTopbar from "../../components/hr/HrTopbar.vue"

const currentPageTitle = ref("Interview Evaluation Criteria")

const isSidebarOpen = ref(true)
const isDarkMode = ref(false)

const showModal = ref(false)
const editing = ref(false)

const search = ref("")

const criteria = ref([])
const roles = ref([])

const form = ref({
  id: null,
  name: "",
  max_score: 10,
})

const fetchCriteria = async () => {
  const res = await api.get("InterviewEvaluationCriteria.php")
  criteria.value = res.data
}

const fetchRoles = async () => {
  const res = await api.get("Positions.php")
  roles.value = res.data
}

const filteredCriteria = computed(() => {
  return criteria.value.filter(c =>
    c.name.toLowerCase().includes(search.value.toLowerCase())
  )
})

const openCreate = () => {
  editing.value = false
  form.value = {
    id: null,
    name: "",
    max_score: 10,
  }
  showModal.value = true
}

const editItem = (item) => {
  editing.value = true
  form.value = { ...item }
  showModal.value = true
}

const saveItem = async () => {
  if (editing.value) {
    await api.put("InterviewEvaluationCriteria.php", form.value)
  } else {
    await api.post("InterviewEvaluationCriteria.php", form.value)
  }
  fetchCriteria()
  closeModal()
}

const deleteItem = async (id) => {
  await api.delete("InterviewEvaluationCriteria.php", { data: { id } })
  criteria.value = criteria.value.filter(i => i.id !== id)
}

const closeModal = () => {
  showModal.value = false
}

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value
}

const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value
  document.documentElement.classList.toggle("dark", isDarkMode.value)
}

onMounted(() => {
  fetchCriteria()
  fetchRoles()
})
</script>

<style>
.modal-enter-active,
.modal-leave-active {
  transition: all .25s ease;
}
.modal-enter-from {
  opacity: 0;
  transform: scale(.9);
}
.modal-leave-to {
  opacity: 0;
  transform: scale(.9);
}
</style>